import * as THREE from "three";
import { Bot } from "./Bot";
import { Tank } from "./Tank";
import { Environment } from "./environment";

/**
 * Klasa zarządzająca botami w grze
 */
export class BotManager {
  private bots: Bot[] = [];
  private scene: THREE.Scene | null = null;
  private environment: Environment | null = null;
  private enabled: boolean = true;
  private botsInitialized: boolean = false;
  private camera: THREE.Camera | null = null;

  private readonly MAX_BOTS = 4; // Maksymalna liczba botów

  /**
   * Tworzy nowego menedżera botów
   */
  constructor() {
    this.bots = [];
    // Remove the immediate bot registration since we'll handle it in addBots
  }

  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
    // Update camera for all existing bots
    this.bots.forEach((bot) => {
      bot.setCamera(camera);
    });
  }

  setEnvironment(environment: Environment): void {
    this.environment = environment;

    // Ustaw środowisko dla wszystkich istniejących botów
    this.bots.forEach((bot) => {
      bot.tank.setEnvironment(environment);
    });
  }

  /**
   * Dodaje istniejącego bota do menedżera
   * @param bot Bot do dodania
   */
  addExistingBot(bot: Bot): void {
    // Check if bot already exists in our collection
    const exists = this.bots.some((existingBot) => existingBot.tank.playerId === bot.tank.playerId);

    if (!exists) {
      this.bots.push(bot);
      console.log(`[BotManager] Added existing bot: ${bot.tank.playerId}`);

      // Set environment if available
      if (this.environment) {
        bot.tank.setEnvironment(this.environment);
      }

      // Set camera if available
      if (this.camera) {
        bot.setCamera(this.camera);
      }
    } else {
      console.log(`[BotManager] Bot ${bot.tank.playerId} already exists in manager`);
    }
  }

  /**
   * Dodaje boty do sceny
   * @param scene Scena gry
   * @param environment Środowisko gry
   */
  addBots(scene: THREE.Scene, environment: Environment): void {
    this.scene = scene;
    this.environment = environment;

    // Check if bots are already initialized globally
    if (this.botsInitialized) {
      console.log("[BotManager] Bots already initialized, skipping creation");
      return;
    }

    // Check if bots exist in gameState
    if (window.gameState?.tanks) {
      const existingBots = window.gameState.tanks.filter((tank) => tank.playerId.startsWith("bot-"));
      if (existingBots.length > 0) {
        console.log(`[BotManager] Found ${existingBots.length} existing bots, skipping bot creation`);
        // Add existing bots to our local management
        existingBots.forEach((tank) => {
          const bot = new Bot(scene, tank);
          if (this.environment) {
            bot.tank.setEnvironment(this.environment);
          }
          if (this.camera) {
            bot.setCamera(this.camera);
          }
          this.bots.push(bot);
        });
        this.botsInitialized = true;
        return;
      }
    }

    // Only create new bots if we're the first player
    if (!window.socket) {
      console.warn("[BotManager] No socket connection available");
      return;
    }

    console.log("[BotManager] Checking if first player...");
    window.socket.emit("isFirstPlayer", (isFirst: boolean) => {
      console.log(`[BotManager] isFirstPlayer response: ${isFirst}`);
      if (!isFirst) {
        console.log("[BotManager] Not first player, skipping bot creation");
        return;
      }

      console.log("[BotManager] First player, creating bots");
      // Create new bots only if none exist
      Bot.createBots(scene, this.MAX_BOTS).forEach((bot) => {
        bot.tank.setEnvironment(environment);
        if (this.camera) {
          bot.setCamera(this.camera);
        }
        this.bots.push(bot);
      });

      this.configureBotTankHandling();
      this.registerBotsWithServer();
      this.botsInitialized = true;

      console.log("[BotManager] Total bots:", this.bots.length);
    });
  }

  public update(deltaTime: number, tanks: Tank[]): void {
    if (!this.enabled || !this.scene || !this.environment) {
      return;
    }

    for (let i = 0; i < this.bots.length; i++) {
      const bot = this.bots[i];
      if (!bot.tank.environment) {
        bot.tank.setEnvironment(this.environment!);
      }
      bot.update(deltaTime, tanks);
    }
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public removeAllBots(): void {
    for (const bot of this.bots) {
      if (this.scene) {
        this.scene.remove(bot.tank.mesh);
      }

      if (window.gameState?.tanks) {
        const index = window.gameState.tanks.indexOf(bot.tank);
        if (index !== -1) {
          window.gameState.tanks.splice(index, 1);
        }
      }
    }

    this.bots = [];
    this.botsInitialized = false;
  }

  public getBotCount(): number {
    return this.bots.length;
  }

  private registerBotsWithServer(): void {
    if (!window.socket) {
      console.warn("[BotManager] No socket connection available for bot registration");
      return;
    }

    this.bots.forEach((bot) => {
      window.socket.emit("registerBot", {
        botId: bot.tank.playerId,
        position: {
          x: bot.tank.mesh.position.x,
          y: bot.tank.mesh.position.y,
          z: bot.tank.mesh.position.z,
        },
        health: bot.tank.health,
        maxHealth: bot.tank.maxHealth,
      });
    });

    console.log("[BotManager] Registered", this.bots.length, "bots with server");
  }

  private configureBotTankHandling(): void {
    if (!window.socket) {
      console.warn("[BotManager] No socket connection available for configuring bot handling");
      return;
    }

    const originalOnTankRespawn = window.socket.listeners("tankRespawn").pop();
    if (originalOnTankRespawn) {
      window.socket.off("tankRespawn", originalOnTankRespawn);
      window.socket.on("tankRespawn", (data: any) => {
        if (data.targetId && data.targetId.startsWith("bot-")) {
          return;
        }
        originalOnTankRespawn(data);
      });
    }
  }
}
